/**
 * homeScene.ts - 3D scene for the home page
 */

import * as THREE from 'three'

import { COMPONENTS, COOKIES } from '@js/global'
import { Thib } from '../models'
import { BaseScene, BaseRaycaster } from '../core'

import css from '@css/sass/styles.module.scss'


/* ___________________________________________________________________________
|                               L O C A L                                     |
|___________________________________________________________________________ */


interface ThibRaycasterCallbacks {
  onThibHover: () => void
  onThibUnhover: () => void
  onThibClick: () => void
}

class ThibRaycaster extends BaseRaycaster<{
  onHover?: (obj: THREE.Object3D) => void
  onUnhover?: (obj: THREE.Object3D) => void
  onClick?: (obj: THREE.Object3D) => void
}> {
  constructor(
    scene: THREE.Scene,
    camera: THREE.Camera,
    callbacks: ThibRaycasterCallbacks
  ) {
    super(scene, camera, {
      onHover: () => callbacks.onThibHover(),
      onUnhover: () => callbacks.onThibUnhover(),
      onClick: () => callbacks.onThibClick()
    })
  }

  protected isTarget(object: THREE.Object3D): boolean {
    return object.name.toUpperCase() === 'THIB_BODY'
  }

  protected shouldStopClick(object: THREE.Object3D): boolean {
    const name = object.name.toUpperCase()
    return name === 'WALLS' || name === 'FLOOR' || name === 'PLINTHES'
  }
}


/* ___________________________________________________________________________
|                          H O M E   S C E N E                               |
|___________________________________________________________________________ */

export class HomeScene extends BaseScene {
  private raycaster: ThibRaycaster | null = null
  private thib!: Thib
  private readonly CENTER = new THREE.Vector3(0, -1, -8)

  constructor() {
    super(css.cnv)
    this.scene.background = new THREE.Color(0xFFFFFF)

    this.setupRenderer(this.canvas, this.camera, this.scene, {
      antialiasing: false,
      composer: true,
      saturation: -0.2,
      contrast: -0.1,
      brightness: 0.1,
    })

  }

  /* -------------------------------------------------------------------------
  |  S E T U P
  |------------------------------------------------------------------------- */


  async loadThib(): Promise<void> {
    this.thib = new Thib({
      transform: {
        position: { x: this.CENTER.x, y: this.CENTER.y, z: this.CENTER.z },
        rotation: { x: 0, y: 0, z: 0 },
        scale: 0.1
      },
      animations: ['walk', 'run', 'jump'],
      animatorOptions: {
        followMouse: true,
        renderer: this.renderer,
        center: this.CENTER,
        camera: this.camera,
      },
      renderOrder: 999
    }, this.camera)

    await this.thib.load(this.scene)

    console.log('[HomeScene] Thib loaded:', this.thib)
  }


  setupInteractions(): void {
    this.raycaster = new ThibRaycaster(this.scene, this.camera, {
      onThibHover:   () => this.onThibHover(),
      onThibUnhover: () => this.onThibUnhover(),
      onThibClick:   () => this.onThibClick(),
    })

    window.addEventListener('mousemove', (e) => this.raycaster!.handleHover(e))
    window.addEventListener('click',     (e) => this.raycaster!.handleClick(e))
  }

  
  /* -------------------------------------------------------------------------
  |  I N T E R A C T I O N S
  |------------------------------------------------------------------------- */

  private onThibHover(): void {
    this.canvas.style.cursor = 'pointer'
  }

  private onThibUnhover(): void {
    console.debug("thib unhovered")
  }
  
  private onThibClick(): void {
    const captcha = COMPONENTS.captcha.object
    if (!captcha) { console.warn('[HomeScene] Captcha not setup'); return }

    captcha.display({
      onConnect: () => this.openChatRoom(),
      onRefresh:  (notif: string) => this.displayNotif(notif),
      notif:      () => this.getCaptchaNotif()
    })
    this.displayNotif(this.getCaptchaNotif())
  }

  private openChatRoom(): void {
    const alertbox = COMPONENTS.alertbox.object
    if (!alertbox) { console.warn('[HomeScene] Alertbox not setup'); return }

    alertbox.display({
      oui_clicked: () => { window.location.href = './chatroom.html' },
      non_clicked: () => { console.log('[HomeScene] Cancelled') }
    })
  }

  private getCaptchaNotif(): string {
    return `Pour entrer dans le salon privé, merci de rentrer le code suivant: <strong>${COOKIES.captchaCode}</strong>`
  }

  private displayNotif(message: string): void {
    const notifbar = COMPONENTS.notifbar.object
    if (!notifbar) { console.warn('[HomeScene] Notifbar not setup'); return }
    notifbar.display(message)
  }

  /* -------------------------------------------------------------------------
  |  L O O P  (implements BaseScene.update)
  |------------------------------------------------------------------------- */

  protected update(delta: number): void {
    this.thib?.update(delta)
  }

  start(): void {
    this.startLoop()
    console.debug('[HomeScene] Render loop started')
  }

  async setup(): Promise<void> {
    await this.loadThib()
    this.setupInteractions()
    this.start()
  }

}


/////////////////////////////////////////////////////////////////
//     DEPRECATED
/////////////////////////////////////////////////////////////////
